<template>
  <div id="form">
    <b-row class="row">
      <b-col cols="5">
        <div class="floating-label textarea" :class="{ disabled: !edit } ">
          <textarea
            class="floating-textarea"
            v-model="assetDescriptionInput"
            ref="assetDescriptionTextInput"
            :label="$t('ASSET_DESCRIPTION')"
            placeholder=" "
            required
            :disabled="!edit"
          >
          </textarea>
          <span class="highlight"></span>
          <label>{{ $t('ASSET_DESCRIPTION') }}<span>*</span></label>
          <small class="red" v-if="!!validateMessageTexarea">{{ validateMessageTexarea }}</small>
        </div>
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <text-input
          :label="$t('EXPECTED_AMOUNT')"
          v-model='requiredAmountInput'
          :hint="changeDigitToViText(requiredAmountInput)"
          :disabled="!edit"
          :validate="validateAmount"
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="5">
        <text-input
          :label="$t('ASSET_TYPE')"
          v-model='order.assetType'
          required
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <text-input
          ref="proposedAmountTextInput"
          :label="$t('VALIDATOR_AMOUNT')"
          v-model='proposedAmountInput'
          :hint="changeDigitToViText(proposedAmountInput)"
          :disabled="!edit"
          :validate="validateAmount"
          required
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="5">
        <text-input
          :label="$t('ACCESSORY')"
          v-model='accessoryInput'
          :disabled="!edit"
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <text-input
          ref="approvedAmountTextInput"
          :label="$t('APPROVED_AMOUNT')"
          v-model='approvedAmountInput'
          :hint="changeDigitToViText(approvedAmountInput)"
          :disabled="!edit"
          :validate="validateApproveAmount"
          required
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="5">
        <div class="floating-label" :class="{ disabled: !edit, active: !!validatorInput }">
          <select-input
            tabindex="-1"
            :items="validatorsOptions"
            v-model="validatorInput"
            ref="validatorsTextInput"
            borderless
            required
            :disabled="!edit"
          />
          <span class="highlight"></span>
          <label>{{ $t("VALIDATOR")}}<span>*</span></label>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch, getCurrentInstance } from '@vue/composition-api';
import TextInput from '@/components/TextInput';
import SelectInput from '@/components/SelectInput';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { changeDigitToViText } from '@/helpers';
import $ from 'jquery';

import { MILLION } from '@/consts';

const { useState, useActions } = createNamespacedHelpers('contractNew');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'AssetForm',
  components: {
    TextInput,
    SelectInput,
  },
  props: {
    edit: {
      type: Boolean,
      default: true,
    },
    value: Boolean,
  },
  setup(props, { refs, emit }) {
    const vm = getCurrentInstance();

    const { order, asset, contract } = useState(['order', 'asset', 'contract']);
    const { setOrder, setAsset, setContract } = useActions(['setOrder', 'setAsset', 'setContract']);
    const { validators } = useAppState(['validators']);

    const assetDescriptionTextInput = ref(null);
    const assetDescriptionInput = ref('');

    const requiredAmountInput = ref('');
    const proposedAmountInput = ref('');
    const accessoryInput = ref('');
    const approvedAmountInput = ref('');

    const validatorInput = ref('');
    const validatorsOptions = computed(() => validators.value.map((item) => ({
      text: item.username,
      value: item.id
    })));

    const setData = () => {
      setOrder({
        requiredAmount: requiredAmountInput.value,
        proposedAmount: proposedAmountInput.value,
      });

      setAsset({
        description: assetDescriptionInput.value,
        meta: {
          accessory: accessoryInput.value
        }
      });

      setContract({
        approvedAmount: approvedAmountInput.value,
        validator: validatorInput.value,
      });
    };

    const setDataFromState = () => {
      assetDescriptionInput.value = asset.value.description;
      requiredAmountInput.value = order.value.requiredAmount;
      proposedAmountInput.value = order.value.proposedAmount;
      approvedAmountInput.value = contract.value.approvedAmount;
      validatorInput.value = contract.value.validator;

      if ('accessory' in asset.value.meta) {
        accessoryInput.value = asset.value.meta.accessory;
      }
    };

    const validateAmount = (val) => (/^-?\d*(\.[0-9]{1,3})?$/.test(val) ? '' : 'INVALID_DATA');

    const validateApproveAmount = (val) => {
      if (val * MILLION > proposedAmountInput.value * MILLION) {
        return 'INVALID_APPROVE_AMOUNT';
      }
      return validateAmount(val);
    };

    // validate textarea and style input textarea
    const validateMessageTexarea = ref('');
    // validate textarea
    const validateTextarea = () => {
      if (!assetDescriptionTextInput.value.validity.valid) {
        validateMessageTexarea.value = vm.$i18n.t('REQUIRED_FIELD');
      } else {
        validateMessageTexarea.value = '';
      }
    };

    watch(order, setDataFromState);
    watch(asset, setDataFromState);
    watch(contract, () => {
      approvedAmountInput.value = contract.value.approvedAmount;
      validatorInput.value = contract.value.validator;
    });
    onMounted(() => {
      setDataFromState();
      validateTextarea();
      refs.proposedAmountTextInput.validateInput();
      refs.approvedAmountTextInput.validateInput();
    });

    watch(proposedAmountInput, () => { refs.approvedAmountTextInput.validateInput(); });

    const valid = computed(() => {
      const conditions = [
        assetDescriptionInput.value.length > 0,
        /^\d+(\.[0-9]{1,3})?$/.test(requiredAmountInput.value),
        /^\d+(\.[0-9]{1,3})?$/.test(approvedAmountInput.value),
        /^\d+(\.[0-9]{1,3})?$/.test(proposedAmountInput.value),
        approvedAmountInput.value * MILLION <= proposedAmountInput.value * MILLION,
        validatorInput.value !== null,
      ];

      return !conditions.includes(false);
    });

    watch(valid, () => emit('input', valid.value));

    const resizeTextArea = () => {
      const currentH = $(assetDescriptionTextInput.value).height();
      $(assetDescriptionTextInput.value).height('auto');
      const h = assetDescriptionTextInput.value.scrollHeight;
      $(assetDescriptionTextInput.value).height(currentH);
      $(assetDescriptionTextInput.value).height(h);
    };

    watch(assetDescriptionInput, () => {
      validateTextarea();
      setTimeout(() => {
        resizeTextArea();
      }, 0);
    });

    return {
      order,
      assetDescriptionTextInput,
      assetDescriptionInput,
      requiredAmountInput,
      proposedAmountInput,
      accessoryInput,
      approvedAmountInput,
      validatorInput,
      validatorsOptions,
      validateAmount,
      validateApproveAmount,
      setData,
      changeDigitToViText,
      validateMessageTexarea,
    };
  },
};
</script>


<style lang='scss' scoped>
@import "~@/assets/scss/colors";

#form {
  .row {
    margin-bottom: 30px;
  }
}

</style>

<style lang='scss'>
@import "~@/assets/scss/colors";
#form {
  .floating-label {
    width: 100%;
    position: relative;
    bottom: 10px;
    .select-input {
      width: 100%;
      small {
        position: absolute;
      }
      .display {
        border-bottom: 1px solid $alto;
        padding-left: 0px;
      }
      &:focus {
        outline: none;
        &~label {
          top:-12px;
          font-size:0.825em;
          color:$blueNcs;
        }
      }

      &:focus{
        &~.highlight{
          width: 100%;
        }
      }

      &.disabled {
        pointer-events: none;
        .display {
          border-bottom: 1px solid $silver;
        }
      }
    }
    label {
      color: $grey;
      font-size:14px;
      font-weight: bold;
      position:absolute;
      pointer-events:none;
      left:0px;
      top:13px;
      transition:0.2s ease all;
      -moz-transition:0.4s ease all;
      -webkit-transition:0.4s ease all;
      span {
        color: $geranium;
      }
    }

    .highlight{
      content:'';
      display: block;
      position: absolute;
      left:0;
      width: 0;
      height: 2px;
      background: $blueNcs;
      transition: all 200ms ease-out;
    }

    .floating-textarea {
      font-size:14px;
      padding: 4px 4px 4px 0px;
      display:block;
      width:100%;
      height:30px;
      border:none;
      border-bottom:1px solid $alto;
      overflow: hidden;
      resize: none;
      min-height: 30px;
      max-height: 400px;
      &:focus, &:not(:placeholder-shown) {
        outline: none;
        &~label {
          top:-12px;
          font-size:0.825em;
          color:$blueNcs;
        }
      }
      &:focus{
        &~.highlight{
          width: 100%;
        }
      }
    }

    &.disabled {
      label {
        color: $silver;
      }
      &:focus, &:not(:placeholder-shown):valid {
        &~label {
          top:-12px;
          font-size: 0.825em;
          color:$blueNcs;
        }
      }
      .floating-textarea {
        border-bottom: 1px solid $silver;
        background-color: unset;
      }
    }

    &.active {
      .select-input{
        &:focus, &:not(.value) {
        outline: none;
          &~label {
            top:-12px;
            font-size:0.825em;
            color:$blueNcs;
          }
        }
      }
    };
  }

  .textarea {
    bottom: 0px;
    label {
      top: 5px;
    }

    small {
      color: $geranium;
    }
  }
}
</style>
